import type {
    Campana,
    CanalCampana,
    Destinatario,
    Disparador,
    EnvioAutomatico,
    EstadoCampana,
    EstadoEnvio,
    EstadoPlantillaMarketing,
    EstadoRegla,
    MotivoDescarte,
    MotivoFallo,
    MotivoOmitido,
    MotivoPausa,
    MotivoPausaRegla,
    Regla,
    Resultados,
    Segmento,
} from '../../api/ops/campanas';
import type { EstadoChip } from '../../componentes/Chip';
import type { NombreIcono } from '../../componentes/iconos';
import type { TarjetaAyuda } from '../../marco/cabecera';
import { dinero } from '../../logica/dinero';

/**
 * Reglas sin estilos de «Campañas» (12.1 a 12.7, `diseno/campanas/`): qué apartados ve cada uno, los textos de cada
 * estado y motivo, qué botones lleva una campaña según su estado y su motivo de pausa, y cómo se cuentan los fallos y
 * descartes. Todo con los textos del diseño.
 */

// ---- Apartados (12.1) ----

export type Apartado = 'campanas' | 'automaticos' | 'plantillas';

export const APARTADOS: readonly { valor: Apartado; etiqueta: string; corta: string; icono: NombreIcono; pista: string }[] = [
    { valor: 'campanas', etiqueta: 'Campañas', corta: 'Campañas', icono: 'send', pista: 'Una campaña es un envío que preparas tú, una vez, a un grupo de clientes.' },
    { valor: 'automaticos', etiqueta: 'Mensajes automáticos', corta: 'Automáticos', icono: 'zap', pista: 'Un mensaje automático es una regla: cuando pasa algo (una visita, un cumpleaños), sale solo con una plantilla.' },
    { valor: 'plantillas', etiqueta: 'Plantillas', corta: 'Plantillas', icono: 'file-text', pista: 'Una plantilla es el texto que aprueba Meta y reutilizas. Puede usarse en automáticos y campañas, o estar sin usar.' },
];

export interface Vista {
    /** Apartados de la barra (vacío si solo ve el resumen o no ve nada). */
    apartados: Apartado[];
    /** Solo «Resultados de campañas» (el Socio: `campanas.resultados.ver_resumen` sin `campanas.campanas.ver`, G776). */
    soloResumen: boolean;
}

/** Qué ve cada uno según sus permisos (la API protege cada operación; esto solo oculta). */
export function vistaDe(puede: (codigo: string) => boolean): Vista {
    if (puede('campanas.campanas.ver')) {
        return { apartados: APARTADOS.map((a) => a.valor), soloResumen: false };
    }
    return { apartados: [], soloResumen: puede('campanas.resultados.ver_resumen') };
}

/** El apartado de `?apartado=`: `resultados` es Campañas con el resumen abierto (el enlace de 7.1) y `ajustes` abre 12.7. */
export function apartadoDe(pedido: string | null, vista: Vista): Apartado | null {
    const primero = vista.apartados[0] ?? null;
    if (pedido === 'resultados' || pedido === 'ajustes') {
        return vista.apartados.includes('campanas') ? 'campanas' : primero;
    }
    return vista.apartados.find((a) => a === pedido) ?? primero;
}

// ---- Canales ----

export const CANAL: Readonly<Record<CanalCampana, { nombre: string; icono: NombreIcono }>> = {
    whatsapp: { nombre: 'WhatsApp', icono: 'message-circle' },
    telegram: { nombre: 'Telegram', icono: 'send' },
    email: { nombre: 'Email', icono: 'mail' },
};

/** «por WhatsApp», «por email». */
export function canalEnFrase(canal: CanalCampana): string {
    return canal === 'email' ? 'email' : CANAL[canal].nombre;
}

// ---- Estados de una campaña ----

/** Chip de cada estado. Lo normal («Enviada») va en texto gris, sin chip (repaso del 18 oct). */
export const ESTADO_CAMPANA: Readonly<Record<EstadoCampana, { texto: string; chip: EstadoChip | null }>> = {
    draft: { texto: 'Borrador', chip: 'draft' },
    scheduled: { texto: 'Programada', chip: 'pending' },
    sending: { texto: 'Enviando', chip: 'pending' },
    paused: { texto: 'Pausada', chip: 'neutral' },
    sent: { texto: 'Enviada', chip: null },
    cancelled: { texto: 'Cancelada', chip: 'problem' },
    stopped: { texto: 'Detenida', chip: 'problem' },
};

/**
 * Filtros de la lista (`status` de listCampaigns), con la cifra de `counts` si llega (G802). «Canceladas» suma las
 * canceladas y las detenidas en una sola llamada (`status=cancelled,stopped`, G827).
 */
export const FILTROS_CAMPANAS: readonly { valor: readonly EstadoCampana[] | null; etiqueta: string }[] = [
    { valor: null, etiqueta: 'Todas' },
    { valor: ['draft'], etiqueta: 'Borradores' },
    { valor: ['scheduled'], etiqueta: 'Programadas' },
    { valor: ['sending'], etiqueta: 'Enviando' },
    { valor: ['paused'], etiqueta: 'Pausadas' },
    { valor: ['sent'], etiqueta: 'Enviadas' },
    { valor: ['cancelled', 'stopped'], etiqueta: 'Canceladas' },
];

/**
 * La cifra de un filtro: la de `counts` (G802) o ninguna (R-4: sin inventarla). «Todas», la suma si llegan todas; un
 * filtro de varios estados, la suma de los que lleguen (`counts.cancelled` no cuenta las detenidas, G827).
 */
export function cifraDeFiltro(valor: EstadoCampana | readonly EstadoCampana[] | null, counts: Readonly<Record<string, number>> | undefined): number | null {
    if (counts === undefined) {
        return null;
    }
    if (valor === null) {
        const todas = Object.values(counts);
        return todas.length === 0 ? null : todas.reduce((a, b) => a + b, 0);
    }
    const llegan = (typeof valor === 'string' ? [valor] : valor).map((v) => counts[v]).filter((n): n is number => n !== undefined);
    return llegan.length === 0 ? null : llegan.reduce((a, b) => a + b, 0);
}

// ---- Pausas (12.3; G713, G744, G746, G762) ----

export interface Pausa {
    titulo: string;
    texto: string;
    /** Pausa por cupo: se reanuda a mano con «Reanudar» hasta Stripe (G746) y se compra en Plan (G561). */
    porCupo: boolean;
    /** Sigue sola cuando vuelve (canal o plantilla, G744, G762): sin «Reanudar». */
    sigueSola: boolean;
}

export const PAUSA: Readonly<Record<MotivoPausa, Pausa>> = {
    sends_exhausted: { titulo: 'Sin envíos', texto: 'Se acabaron los envíos de este mes del bloque Mensajes y campañas.', porCupo: true, sigueSola: false },
    marketing_pack_exhausted: { titulo: 'Sin paquete de marketing', texto: 'Se acabó tu paquete de marketing de WhatsApp: lo incluido en tu plan y el paquete extra Marketing.', porCupo: true, sigueSola: false },
    season_pause: { titulo: 'Pausa de temporada', texto: 'Tu negocio está en pausa de temporada: no sale ninguna campaña.', porCupo: false, sigueSola: false },
    template_unavailable: { titulo: 'Plantilla pausada', texto: 'Meta ha pausado la plantilla de esta campaña. Sigue sola cuando Meta la vuelva a aprobar.', porCupo: false, sigueSola: true },
    channel_unavailable: { titulo: 'Canal caído', texto: 'El canal de esta campaña no está conectado o se ha caído. Sigue sola en cuanto vuelva.', porCupo: false, sigueSola: true },
    no_entitlement: { titulo: 'Sin el bloque', texto: 'Se quitó el bloque Mensajes y campañas.', porCupo: false, sigueSola: false },
};

/** «Pausada: sin paquete de marketing». */
export function tituloPausa(motivo: MotivoPausa | null | undefined): string {
    const p = motivo == null ? null : PAUSA[motivo];
    return p === null ? 'Pausada' : `Pausada: ${p.titulo.charAt(0).toLocaleLowerCase('es') + p.titulo.slice(1)}`;
}

// ---- Acciones de la ficha (12.3) ----

export type AccionCampana = 'editar' | 'desprogramar' | 'cancelar' | 'detener' | 'comprar' | 'reanudar' | 'pasar-a-email' | 'duplicar' | 'seguir';

export interface QuienActua {
    puede: (codigo: string) => boolean;
    soloLectura: boolean;
    /** Comprar paquetes es cosa del propietario. */
    propietario: boolean;
}

/**
 * Botones de la franja de estado de una campaña, en el orden del diseño. Detener y cancelar también en solo lectura
 * (G70: parar no escribe nada nuevo). «Reanudar» solo en las pausas por cupo (G746); por canal o plantilla sigue sola
 * (G744, G762). Comprar lleva a Plan mientras no haya Stripe (G561).
 */
export function accionesDe(c: Pick<Campana, 'status' | 'pause_reason' | 'channel'>, q: QuienActua): AccionCampana[] {
    const crea = q.puede('campanas.campanas.crear') && !q.soloLectura;
    const envia = q.puede('campanas.campanas.enviar') && !q.soloLectura;
    const cancela = q.puede('campanas.campanas.cancelar');
    switch (c.status) {
        case 'draft':
            return crea ? ['seguir'] : [];
        case 'scheduled':
            return [...(crea ? (['editar'] as const) : []), ...(envia ? (['desprogramar'] as const) : []), ...(cancela ? (['cancelar'] as const) : [])];
        case 'sending':
            return cancela ? ['detener'] : [];
        case 'paused': {
            const p = c.pause_reason == null ? null : PAUSA[c.pause_reason];
            const cupo = p?.porCupo === true;
            return [
                ...(cupo && q.propietario && !q.soloLectura ? (['comprar'] as const) : []),
                ...(cupo && envia ? (['reanudar'] as const) : []),
                ...(c.channel !== 'email' && crea ? (['pasar-a-email'] as const) : []),
                ...(cancela ? (['detener'] as const) : []),
            ];
        }
        case 'sent':
        case 'stopped':
        case 'cancelled':
            return crea ? ['duplicar'] : [];
    }
}

/** El texto del botón de comprar según el motivo (lleva a Plan, 1.15). */
export function textoComprar(motivo: MotivoPausa | null | undefined): string {
    return motivo === 'sends_exhausted' ? 'Ampliar envíos' : 'Comprar paquete Marketing';
}

// ---- Resultados (12.4; G743, G764) ----

export const FALLO: Readonly<Record<MotivoFallo, string>> = {
    // G764: `invalid_recipient` no es un fallo: se descarta como «Sin contacto» (ver `motivosDe`).
    invalid_recipient: 'Sin contacto en ese canal',
    blocked_by_user: 'Nos tiene bloqueados',
    rate_limited: 'Límite de marketing de Meta',
    hard_bounce: 'El email no existe',
    soft_bounce: 'Buzón lleno o no disponible',
    complaint: 'Lo marcó como no deseado',
    provider_rejected: 'El proveedor lo rechazó',
    provider_error: 'Fallo del proveedor',
};

export const DESCARTE: Readonly<Record<MotivoDescarte, string>> = {
    no_marketing_consent: 'Retiró el permiso antes del envío',
    no_contact: 'Sin contacto en ese canal',
    season_pause: 'Pausa de temporada',
    marketing_quota_exhausted: 'Sin saldo',
    profile_anonymized: 'Sus datos se borraron',
    channel_unavailable: 'El canal no estaba disponible',
    campaign_stopped: 'La campaña se detuvo',
};

export interface Motivo {
    clave: string;
    texto: string;
    cifra: number;
    tipo: 'fallo' | 'descarte';
}

/**
 * Por qué no llegó: fallos y descartes por motivo. `invalid_recipient` se suma a «Sin contacto» (`no_contact`) entre los
 * descartes (G764). Un motivo que el panel no conoce sale con su código.
 */
export function motivosDe(r: Pick<Resultados, 'failed_by_reason' | 'discarded_by_reason'>): Motivo[] {
    const descartes: Record<string, number> = { ...(r.discarded_by_reason ?? {}) };
    const fallos: Record<string, number> = { ...(r.failed_by_reason ?? {}) };
    const invalidos = fallos.invalid_recipient ?? 0;
    delete fallos.invalid_recipient;
    if (invalidos > 0) {
        descartes.no_contact = (descartes.no_contact ?? 0) + invalidos;
    }
    const textoF = (k: string) => (FALLO as Record<string, string>)[k] ?? k;
    const textoD = (k: string) => (DESCARTE as Record<string, string>)[k] ?? k;
    return [
        ...Object.entries(fallos).filter(([, n]) => n > 0).map(([k, n]) => ({ clave: `f-${k}`, texto: textoF(k), cifra: n, tipo: 'fallo' as const })),
        ...Object.entries(descartes).filter(([, n]) => n > 0).map(([k, n]) => ({ clave: `d-${k}`, texto: textoD(k), cifra: n, tipo: 'descarte' as const })),
    ];
}

/** Fallidos y descartados con G764: los `invalid_recipient` pasan de fallidos a «no salieron». */
export function fallidosYDescartados(r: Pick<Resultados, 'failed' | 'discarded' | 'failed_by_reason'>): { fallidos: number; descartados: number } {
    const invalidos = r.failed_by_reason?.invalid_recipient ?? 0;
    return { fallidos: Math.max(0, r.failed - invalidos), descartados: (r.discarded ?? 0) + invalidos };
}

export const ESTADO_DESTINATARIO: Readonly<Record<EstadoEnvio, { texto: string; chip: EstadoChip | null }>> = {
    queued: { texto: 'En cola', chip: 'neutral' },
    requested: { texto: 'Encargado', chip: 'neutral' },
    sent: { texto: 'Enviado', chip: 'neutral' },
    delivered: { texto: 'Entregado', chip: null },
    read: { texto: 'Leído', chip: null },
    failed: { texto: 'Fallido', chip: 'problem' },
    discarded: { texto: 'Descartado', chip: 'neutral' },
};

/** Estado y motivo de un destinatario como se ven: `invalid_recipient` es «Descartado · Sin contacto en ese canal» (G764). */
export function lineaDestinatario(d: Pick<Destinatario, 'status' | 'failure_reason' | 'discard_reason'>): { estado: EstadoEnvio; motivo: string | null } {
    if (d.status === 'failed' && d.failure_reason === 'invalid_recipient') {
        return { estado: 'discarded', motivo: DESCARTE.no_contact };
    }
    if (d.status === 'failed') {
        return { estado: 'failed', motivo: d.failure_reason == null ? null : FALLO[d.failure_reason] };
    }
    if (d.status === 'discarded') {
        return { estado: 'discarded', motivo: d.discard_reason == null ? null : DESCARTE[d.discard_reason] };
    }
    return { estado: d.status, motivo: null };
}

/** «78 %» (vacío si no hay base). */
export function porcentaje(parte: number | null | undefined, total: number | null | undefined): string {
    if (parte == null || total == null || total === 0) {
        return '';
    }
    return `${String(Math.round((parte / total) * 100))} %`;
}

// ---- A quién (12.2) ----

/** «Etiquetas: VIP o Cenas de grupo · al menos 2 visitas». Vacío: «Todos los que tienen permiso». */
export function textoSegmento(s: Segmento | undefined, etiquetas: Readonly<Record<string, string>> = {}): string {
    if (s === undefined) {
        return 'Todos los que tienen permiso';
    }
    const partes: string[] = [];
    const nombres = (ids: string[]) => ids.map((i) => etiquetas[i] ?? 'Etiqueta').join(' o ');
    if ((s.tag_ids_any ?? []).length > 0) {
        partes.push(`Etiquetas: ${nombres(s.tag_ids_any ?? [])}`);
    }
    if ((s.tag_ids_all ?? []).length > 0) {
        partes.push(`Etiquetas: ${(s.tag_ids_all ?? []).map((i) => etiquetas[i] ?? 'Etiqueta').join(' y ')}`);
    }
    if (s.last_visit_before != null) {
        partes.push('sin venir desde hace un tiempo');
    }
    if (s.last_visit_after != null) {
        partes.push('vino hace poco');
    }
    if (s.visits_min != null) {
        partes.push(`al menos ${String(s.visits_min)} ${s.visits_min === 1 ? 'visita' : 'visitas'}`);
    }
    if ((s.service_ids_any ?? []).length > 0 || (s.event_ids_any ?? []).length > 0) {
        partes.push('vino a un servicio o evento');
    }
    if (s.total_spent_min != null && s.total_spent_min.amount > 0) {
        partes.push(`ha gastado al menos ${dinero(s.total_spent_min)}`);
    }
    return partes.length === 0 ? 'Todos los que tienen permiso' : partes.join(' · ').replace(/^./, (l) => l.toLocaleUpperCase('es'));
}

// ---- Mensajes automáticos (12.5) ----

export const DISPARADOR: Readonly<Record<Disparador, { texto: string; icono: NombreIcono; explica: string }>> = {
    post_visit: { texto: 'Después de la visita', icono: 'calendar-check', explica: 'Unas horas después de completarse una reserva, una cita o la llegada a un evento.' },
    next_day: { texto: 'Al día siguiente', icono: 'sun', explica: 'El día después de la visita, a una hora fija.' },
    first_visit: { texto: 'Primera visita', icono: 'sparkles', explica: 'Como «después de la visita», solo la primera vez que viene.' },
    birthday: { texto: 'Cumpleaños', icono: 'gift', explica: 'El día de su cumpleaños, una vez al año, a una hora fija.' },
    inactive: { texto: 'Clientes inactivos', icono: 'moon', explica: 'Tras unos días sin venir y sin ninguna visita futura.' },
};

export const ESTADO_REGLA: Readonly<Record<EstadoRegla, { texto: string; chip: EstadoChip | null }>> = {
    draft: { texto: 'Borrador', chip: 'draft' },
    active: { texto: 'Activo', chip: null },
    paused: { texto: 'Pausado', chip: 'pending' },
    archived: { texto: 'Archivado', chip: 'neutral' },
};

/** Aviso de una regla pausada, por motivo (G744; G762: la plantilla se activa sola, sin «reanúdalo»). */
export const PAUSA_REGLA: Readonly<Record<MotivoPausaRegla, string>> = {
    manual: 'La pausó alguien del equipo.',
    template_unavailable: 'Meta ha pausado su plantilla. Se activa sola cuando Meta la vuelva a aprobar.',
    channel_unavailable: 'Su canal no está conectado o se ha caído. Se activa solo en cuanto vuelva.',
    no_entitlement: 'Se quitó el bloque Mensajes y campañas.',
};

export type AccionRegla = 'editar' | 'activar' | 'pausar' | 'archivar';

/**
 * Botones de una regla: activar un borrador o una pausada a mano; pausar una activa; archivar (también un borrador, G742).
 * Pausar y archivar también en solo lectura. Una pausada por su plantilla o su canal no se activa a mano: sigue sola.
 */
export function accionesRegla(r: Pick<Regla, 'status' | 'pause_reason'>, q: Pick<QuienActua, 'puede' | 'soloLectura'>): AccionRegla[] {
    if (!q.puede('campanas.automaticos.gestionar') || r.status === 'archived') {
        return [];
    }
    const escribe = !q.soloLectura;
    const sola = r.status === 'paused' && (r.pause_reason === 'template_unavailable' || r.pause_reason === 'channel_unavailable');
    return [
        ...(escribe ? (['editar'] as const) : []),
        ...(escribe && (r.status === 'draft' || (r.status === 'paused' && !sola)) ? (['activar'] as const) : []),
        ...(r.status === 'active' ? (['pausar'] as const) : []),
        'archivar',
    ];
}

/** «2 h después de la visita», «Al día siguiente, a las 12:00» o «a la hora de siempre» (G740, G748), «Tras 90 días sin venir». */
export function duracion(min: number): string {
    if (min % 1440 === 0) {
        return min === 1440 ? '1 día' : `${String(min / 1440)} días`;
    }
    if (min % 60 === 0) {
        return `${String(min / 60)} h`;
    }
    return min < 60 ? `${String(min)} min` : `${String(Math.floor(min / 60))} h ${String(min % 60)} min`;
}

export function cuandoSale(disparador: Disparador, p: { delay_minutes?: number; send_local_time?: string; inactive_days?: number }): string {
    const hora = p.send_local_time === undefined ? 'a la hora de siempre' : `a las ${p.send_local_time}`;
    switch (disparador) {
        case 'post_visit':
            return `${duracion(p.delay_minutes ?? 0)} después de la visita`;
        case 'first_visit':
            return `${duracion(p.delay_minutes ?? 0)} después de la primera visita`;
        case 'next_day':
            return `Al día siguiente, ${hora}`;
        case 'birthday':
            return `En su cumpleaños, ${hora}`;
        case 'inactive':
            return `Tras ${String(p.inactive_days ?? 0)} días sin venir`;
    }
}

/** La hora de siempre (G740): el inicio de la franja de 12.7 o, sin ella, las 10:00. */
export function horaPorDefecto(franja: { from: string } | null | undefined): string {
    return franja?.from ?? '10:00';
}

export const OMITIDO: Readonly<Record<MotivoOmitido, string>> = {
    frequency_cap: 'Límite de frecuencia',
    no_consent: 'Sin permiso comercial en el canal',
    quota: 'Sin envíos o sin paquete de marketing',
    template_unavailable: 'Plantilla no aprobada',
    channel_unavailable: 'Canal caído o sin conectar',
    season_pause: 'Pausa de temporada',
    no_entitlement: 'Sin el bloque Mensajes y campañas',
    not_eligible: 'Ya no cumple la regla',
};

/** Estado de un envío automático (12.5 › Envíos). */
export const ESTADO_ENVIO_AUTOMATICO: Readonly<Record<EnvioAutomatico['status'], { texto: string; chip: EstadoChip | null }>> = {
    scheduled: { texto: 'Programado', chip: 'neutral' },
    cancelled: { texto: 'Anulado', chip: 'problem' },
    skipped: { texto: 'No enviado', chip: 'neutral' },
    requested: { texto: 'En cola', chip: 'pending' },
    sent: { texto: 'Enviado', chip: 'neutral' },
    delivered: { texto: 'Entregado', chip: null },
    read: { texto: 'Leído', chip: null },
    failed: { texto: 'No ha llegado', chip: 'problem' },
    discarded: { texto: 'No enviado', chip: 'neutral' },
};

/** Por qué no salió un envío automático; `invalid_recipient` como «Sin contacto» (G764). */
export function motivoEnvio(e: Pick<EnvioAutomatico, 'status' | 'skip_reason' | 'discard_reason' | 'failure_reason'>): { estado: EnvioAutomatico['status']; motivo: string | null } {
    if (e.status === 'failed' && e.failure_reason === 'invalid_recipient') {
        return { estado: 'discarded', motivo: DESCARTE.no_contact };
    }
    if (e.skip_reason != null) {
        return { estado: e.status, motivo: OMITIDO[e.skip_reason] };
    }
    if (e.discard_reason != null) {
        return { estado: e.status, motivo: DESCARTE[e.discard_reason] };
    }
    if (e.failure_reason != null) {
        return { estado: e.status, motivo: FALLO[e.failure_reason] };
    }
    return { estado: e.status, motivo: null };
}

// ---- Plantillas (12.6, G448) ----

export const ESTADO_PLANTILLA: Readonly<Record<EstadoPlantillaMarketing, { texto: string; chip: EstadoChip | null }>> = {
    approved: { texto: 'Aprobada', chip: null },
    paused: { texto: 'En pausa', chip: 'problem' },
    rejected: { texto: 'Rechazada', chip: 'problem' },
    withdrawn: { texto: 'Retirada', chip: 'neutral' },
};

/** Canales › Plantillas de Meta (11.4): ahí se crean, se corrigen y se retiran (G275). */
export const RUTA_PLANTILLAS_META = '/canales?apartado=plantillas';
/** Comprar envíos o el paquete Marketing lleva a la pantalla del plan (1.15) mientras no haya Stripe (G561, G746). */
export const RUTA_PLAN = '/plan';

// ---- Ajustes (12.7, G34, G454, G455) ----

export interface ErroresAjustes {
    frecuencia?: string;
    atribucion?: string;
    franja?: string;
}

export function erroresAjustes(a: { frecuencia: string; atribucion: string; conFranja: boolean; desde: string; hasta: string }): ErroresAjustes {
    const e: ErroresAjustes = {};
    const entero = (t: string) => (/^\d+$/.test(t.trim()) ? Number(t.trim()) : NaN);
    const f = entero(a.frecuencia);
    if (!(f >= 1 && f <= 60)) {
        e.frecuencia = 'De 1 a 60 días.';
    }
    const at = entero(a.atribucion);
    if (!(at >= 1 && at <= 30)) {
        e.atribucion = 'De 1 a 30 días.';
    }
    if (a.conFranja) {
        const hora = /^([01]\d|2[0-3]):[0-5]\d$/;
        if (!hora.test(a.desde) || !hora.test(a.hasta)) {
            e.franja = 'Escribe las horas como 09:00.';
        } else if (a.desde >= a.hasta) {
            e.franja = 'La hora de fin tiene que ser después de la de inicio.';
        }
    }
    return e;
}

// ---- «?» de la cabecera ----

/** Línea bajo el título (0.1, PAGES de `diseno/plataforma/0.1-marco-del-panel.html`); el Socio solo ve resultados. */
export const SUBTITULO = { equipo: 'Escríbele otra vez a quien ya vino: campañas, mensajes automáticos y sus plantillas.', socio: 'Resultados de tus campañas y mensajes automáticos.' } as const;

export const AYUDA: Readonly<Record<'campanas' | 'socio' | 'automaticos' | 'plantillas', readonly TarjetaAyuda[]>> = {
    campanas: [
        { icono: 'send', titulo: 'Campañas', texto: 'Un mensaje a un grupo de clientes, ahora o el día y la hora que elijas. Por WhatsApp, Telegram o email.' },
        { icono: 'shield-check', titulo: 'Solo con permiso', texto: 'Solo la reciben quienes aceptaron recibir ofertas y novedades por ese canal. Los demás se cuentan, nunca se nombran.' },
        { icono: 'chart-column', titulo: 'Resultados', texto: 'Entregados, leídos, bajas y las reservas y compras que llegan en los 7 días siguientes.' },
    ],
    socio: [
        { icono: 'chart-column', titulo: 'Resultados', texto: 'Cifras de todas las campañas y mensajes automáticos del periodo, sin datos de ningún cliente.' },
        { icono: 'calendar-check', titulo: 'Reservas y compras posteriores', texto: 'Lo que reservaron o compraron quienes la recibieron en los 7 días siguientes. No es una causa segura.' },
    ],
    automaticos: [
        { icono: 'zap', titulo: 'Mensajes automáticos', texto: 'Una regla: cuando pasa algo (una visita, un cumpleaños), sale solo. Cada cambio es una versión nueva.' },
        { icono: 'shield-check', titulo: 'Solo con permiso', texto: 'Solo lo reciben quienes aceptaron ofertas por ese canal, como mucho uno cada pocos días (Ajustes).' },
    ],
    plantillas: [
        { icono: 'file-text', titulo: 'Plantillas de marketing', texto: 'Por WhatsApp solo se puede escribir con plantillas que Meta haya aprobado. Se crean y se retiran en Canales › Plantillas de Meta.' },
    ],
};

// ---- Periodos del resumen (12.4) ----

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

export interface Periodo {
    valor: '30d' | 'mes' | 'anterior';
    etiqueta: string;
    from: string;
    to: string;
}

const iso = (d: Date) => `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Últimos 30 días, este mes y el mes anterior (por su nombre: «Septiembre»), en fechas del negocio (`from`, `to`). */
export function periodos(hoy: Date = new Date()): Periodo[] {
    const dia = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    const hace29 = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate() - 29);
    const primero = new Date(dia.getFullYear(), dia.getMonth(), 1);
    const anteriorIni = new Date(dia.getFullYear(), dia.getMonth() - 1, 1);
    const anteriorFin = new Date(dia.getFullYear(), dia.getMonth(), 0);
    const nombre = MESES[anteriorIni.getMonth()] ?? '';
    return [
        { valor: '30d', etiqueta: 'Últimos 30 días', from: iso(hace29), to: iso(dia) },
        { valor: 'mes', etiqueta: 'Este mes', from: iso(primero), to: iso(dia) },
        { valor: 'anterior', etiqueta: nombre.charAt(0).toLocaleUpperCase('es') + nombre.slice(1), from: iso(anteriorIni), to: iso(anteriorFin) },
    ];
}

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/**
 * El periodo anterior escrito (12.4; G828, G892): la misma duración justo antes de `from`, como lo calcula
 * `compare=previous_period`. «los 30 días justo antes (15 ago – 13 sep)», «los 13 días justo antes (18–30 sep)»; de un
 * día, «el día justo antes (30 sep)». Con fechas que no se entienden, `null`.
 */
export function periodoAnterior(from: string, to: string): string | null {
    const leer = (t: string) => {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
        return m === null ? null : new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    };
    const ini = leer(from);
    const fin = leer(to);
    if (ini === null || fin === null || fin < ini) {
        return null;
    }
    const dias = Math.round((Date.UTC(fin.getFullYear(), fin.getMonth(), fin.getDate()) - Date.UTC(ini.getFullYear(), ini.getMonth(), ini.getDate())) / 86_400_000) + 1;
    const antesFin = new Date(ini.getFullYear(), ini.getMonth(), ini.getDate() - 1);
    const antesIni = new Date(ini.getFullYear(), ini.getMonth(), ini.getDate() - dias);
    const mes = (d: Date) => MESES_CORTOS[d.getMonth()] ?? '';
    const anio = (d: Date) => (antesIni.getFullYear() === antesFin.getFullYear() ? '' : ` ${String(d.getFullYear())}`);
    const rango =
        dias === 1
            ? `${String(antesFin.getDate())} ${mes(antesFin)}`
            : antesIni.getMonth() === antesFin.getMonth() && antesIni.getFullYear() === antesFin.getFullYear()
              ? `${String(antesIni.getDate())}–${String(antesFin.getDate())} ${mes(antesFin)}`
              : `${String(antesIni.getDate())} ${mes(antesIni)}${anio(antesIni)} – ${String(antesFin.getDate())} ${mes(antesFin)}${anio(antesFin)}`;
    return dias === 1 ? `el día justo antes (${rango})` : `los ${String(dias)} días justo antes (${rango})`;
}

/**
 * Las fechas de un periodo escritas (12.5, G828): «del 14 sep al 13 oct», «del 1 al 13 oct»; con años distintos, con el
 * año. Con fechas que no se entienden, `null`.
 */
export function delAl(from: string, to: string): string | null {
    const leer = (t: string) => {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
        return m === null ? null : { a: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]) };
    };
    const ini = leer(from);
    const fin = leer(to);
    if (ini === null || fin === null) {
        return null;
    }
    const mes = (m: number) => MESES_CORTOS[m] ?? '';
    const otroAnio = ini.a !== fin.a;
    const desde = ini.m === fin.m && !otroAnio ? String(ini.d) : `${String(ini.d)} ${mes(ini.m)}${otroAnio ? ` ${String(ini.a)}` : ''}`;
    return `del ${desde} al ${String(fin.d)} ${mes(fin.m)}${otroAnio ? ` ${String(fin.a)}` : ''}`;
}

/**
 * Chip de una cifra del resumen frente al periodo anterior (12.4, §21; G828, `previous_totals`): «+12 %» o «−3,4 %» (con
 * un decimal por debajo del 10 %), «0 %» si apenas cambia. El color dice si es bueno: subir es bueno salvo en las bajas
 * (`menos`). Sin cifra anterior (o 0), sin chip.
 */
export function variacion(actual: number | null | undefined, anterior: number | null | undefined, menos = false): { texto: string; tono: 'bueno' | 'malo' | 'neutro' } | null {
    if (typeof actual !== 'number' || typeof anterior !== 'number' || anterior === 0) {
        return null;
    }
    const c = ((actual - anterior) / anterior) * 100;
    const a = Math.abs(c);
    if (a < 0.5) {
        return { texto: '0 %', tono: 'neutro' };
    }
    const texto = `${c > 0 ? '+' : '−'}${a >= 10 ? String(Math.round(a)) : a.toFixed(1).replace('.', ',')} %`;
    return { texto, tono: c > 0 !== menos ? 'bueno' : 'malo' };
}
